import {
  type Identifier,
  useDataProvider,
  useGetIdentity,
  useGetOne,
  useGetRecordRepresentation,
  useNotify,
  useTranslate,
  useUpdate,
} from "ra-core";
import { CreateSheet } from "../misc/CreateSheet";
import type { Contact, Task } from "../types";
import { TaskFormContent } from "./TaskFormContent";
import { useQueryClient } from "@tanstack/react-query";

export interface TaskCreateSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contact_id?: Identifier;
}

export const TaskCreateSheet = ({
  open,
  onOpenChange,
  contact_id,
}: TaskCreateSheetProps) => {
  const { identity } = useGetIdentity();
  const translate = useTranslate();
  const getContactRepresentation = useGetRecordRepresentation("contacts");

  const selectContact = contact_id == null;
  const { data: contact } = useGetOne(
    "contacts",
    { id: contact_id! },
    { enabled: !selectContact },
  );
  const [update] = useUpdate();
  const dataProvider = useDataProvider();
  const queryClient = useQueryClient();
  const notify = useNotify();

  if (!identity) return null;

  const handleSuccess = async (data: Task) => {
    // A task can now be about an account or a deal and name nobody, and only a
    // person has a "last seen" to move. The sheet still closes either way.
    if (data.contact_id != null) {
      const { data: taskContact } = await dataProvider.getOne<Contact>(
        "contacts",
        { id: data.contact_id },
      );
      if (taskContact) {
        await update("contacts", {
          id: data.contact_id,
          data: { last_seen: new Date().toISOString() },
          previousData: taskContact,
        });
        queryClient.invalidateQueries({
          queryKey: ["contacts", "getOne"],
        });
      }
    }

    notify("resources.tasks.added");
    // No redirect, only close the sheet
    onOpenChange(false);
  };

  return (
    <CreateSheet
      resource="tasks"
      title={
        <span className="text-xl font-semibold truncate pr-10">
          {!selectContact
            ? translate("resources.tasks.dialog.create_for", {
                name: getContactRepresentation(contact!),
              })
            : translate("resources.tasks.dialog.create")}
        </span>
      }
      redirect={false}
      // The owner is chosen in the form (see TaskFormContent), which defaults
      // it to the logged-in user, so this sheet no longer pins it.
      record={{
        type: "none",
        contact_id,
        due_date: new Date().toISOString(),
      }}
      mutationOptions={{
        onSuccess: handleSuccess,
      }}
      open={open}
      onOpenChange={onOpenChange}
    >
      <TaskFormContent selectContact={selectContact} />
    </CreateSheet>
  );
};
